import useDebounce from 'modules/reports/hooks/debounce'
import Model from 'modules/reports/orm/Model'
import Response from 'modules/reports/orm/Response'
import {SearchContextProps} from './useSearch.context'
import useSearchFetch from './useSearch.fetch'
import useSearchSetters from './useSearch.setters'
import useSearchSort from './useSearch.sort'
import {SearchState, useSearchState} from './useSearch.state'
import {useRef} from 'react'

type Fetcher<T> = (data: any) => Promise<Response<T[]>>

function useSearch<TModel extends Model = any, TParams = {}, TFilters = {}, TDisplay = {}>(
  fetcher: Fetcher<TModel>,
  defaultState: Partial<SearchState<TModel, TParams, TFilters, TDisplay>>
): SearchContextProps<TModel, TParams, TFilters, TDisplay> {
  const searchState = useSearchState(defaultState)
  const [state] = searchState

  const rowCapacity = useRef<number | undefined>()

  const searchSort = useSearchSort(state.sort)
  const searchFetch = useSearchFetch(searchState, debouncedFetcher, rowCapacity.current || 0)
  const setters = useSearchSetters(searchState, searchFetch)
  const queryDebounce = useDebounce(state.query, () => searchFetch({data: [], total: 0}))

  function debouncedFetcher(data: any) {
    queryDebounce.clear()
    return fetcher(data)
  }

  function fetchOffset(start: number, end: number) {
    return searchFetch({}, {start, end})
  }

  function setRowCapacity(rows: number) {
    const firstLoad = rowCapacity.current === undefined
    rowCapacity.current = rows

    if (firstLoad) return fetchOffset(0, rows)
  }

  return {
    ...state,
    ...searchSort,
    ...setters,
    fetchOffset,
    setRowCapacity,
  }
}

export default useSearch
